import { initialize, type LDClient, type LDContext } from 'launchdarkly-react-client-sdk';

import { CILogger } from 'common/log/log.provider';

import { getDevToolbarLDPlugins } from './devToolbar';
import { buildAnonymousLDContext } from './launchDarklyContext';
import { createObservabilityPlugin } from './observability';
import { createSessionReplayPlugin } from './sessionReplay';

const INIT_TIMEOUT_SECONDS = 5;

let client: LDClient | undefined;
let clientPromise: Promise<LDClient | undefined> | undefined;

export const getLDClientSideId = (): string => process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID ?? '';

export const isLaunchDarklyConfigured = (): boolean => getLDClientSideId() !== '';

const createClient = async (): Promise<LDClient | undefined> => {
    if (!isLaunchDarklyConfigured()) {
        return undefined;
    }

    // `bootstrap: 'localStorage'` makes the previous session's values readable synchronously the
    // instant initialize() returns, so a sync read during page load gets the real value rather than
    // the fallback. Replay/observability only register here; they capture nothing until
    // useSessionReplay starts them.
    client = initialize(getLDClientSideId(), buildAnonymousLDContext(), {
        bootstrap: 'localStorage',
        plugins: [createSessionReplayPlugin(), createObservabilityPlugin(), ...getDevToolbarLDPlugins()],
    });

    try {
        await client.waitForInitialization(INIT_TIMEOUT_SECONDS);
    } catch (error) {
        CILogger.getInstance().error('[LaunchDarkly] initialization failed; using cached flags', error);
    }

    return client;
};

/** One client per page load, cached, so `LDProvider` and every `identify` share a single stream. */
export const getOrCreateLDClient = (): Promise<LDClient | undefined> => {
    clientPromise ??= createClient();

    return clientPromise;
};

/**
 * Synchronous read for non-React callers. Serves bootstrapped values before initialization
 * finishes, and `fallback` only when no client exists or nothing is cached yet.
 */
export const getLDFlag = (flagKey: string, fallback: boolean): boolean => {
    if (!client) {
        return fallback;
    }

    const value: unknown = client.variation(flagKey, fallback);

    return Boolean(value);
};

/** Awaits the cached client so a login landing mid-bootstrap still applies. */
export const identifyLDUser = async (context: LDContext): Promise<void> => {
    const client = await getOrCreateLDClient();

    if (!client) {
        return;
    }

    try {
        await client.identify(context);
    } catch (error) {
        CILogger.getInstance().error('[LaunchDarkly] identify failed; flags stay on the previous context', error);
    }
};

export const resetLaunchDarkly = (): void => {
    client = undefined;
    clientPromise = undefined;
};
